import {
  CreateUserPaymentDetailsInputDto,
  UpdateUserPaymentDetailsInputDto,
} from '@starsoft/common/dto';
import { UserPaymentDetailsTypeId } from '@starsoft/common/enums';
import {
  useCoallescedUserPaymentTypeId,
  useKycMeByOriginIdWithOrigin,
  useSearchParams,
} from '@starsoft/common/hooks';
import { Nullable } from '@starsoft/common/interfaces';
import { parseToBigInt } from '@starsoft/common/utils/parsers';
import { useEffect } from 'react';
import { useFormContext } from 'react-hook-form';
import { useUserPaymentDetailsById } from '../../../../../../../../api';
import UserPaymentDetailsFormContentBankAccount from './BankAccount';
import UserPaymentDetailsFormContentImps from './Imps';
import UserPaymentDetailsFormContentPix from './Pix';

/**
 * UserPaymentDetailsFormContentFields Component
 * This component is responsible for rendering the appropriate form fields based on the user payment type.
 * It utilizes various hooks to manage form state and fetch necessary data.
 *
 * @returns {JSX.Element | undefined} The rendered component displaying the form fields for the user payment details.
 *
 * @example
 * // Usage example:
 * <UserPaymentDetailsFormContentFields />
 */
export function UserPaymentDetailsFormContentFields() {
  // Access the form context to set form values
  const { setValue } = useFormContext<
    CreateUserPaymentDetailsInputDto & UpdateUserPaymentDetailsInputDto
  >();

  // Get the coalesced user payment type ID, which can be null
  const typeId: Nullable<string> = useCoallescedUserPaymentTypeId();

  // Retrieve search parameters from the URL
  const search: URLSearchParams = useSearchParams();
  const userPaymentDetails = search.get('user-payment');
  const isCreating: boolean = userPaymentDetails == 'new';

  // Fetch user payment details by ID, determining if the data is loading
  const { isLoading: isLoadingUserPaymentDetails } = useUserPaymentDetailsById({
    id: isCreating
      ? undefined
      : (parseToBigInt(userPaymentDetails ?? '0') as bigint),
  });

  // Fetch KYC data and determine if it is pending or successful
  const {
    isPending: isPendingKyc,
    kyc,
    isSuccess: isKycSuccess,
  } = useKycMeByOriginIdWithOrigin();
  const isPending: boolean = isLoadingUserPaymentDetails || isPendingKyc;

  /**
   * onFetchKyc Function
   * This function sets the identity ID in the form context if KYC data is successfully fetched.
   */
  function onFetchKyc() {
    if (!kyc || !isKycSuccess) {
      return;
    }

    // Set the identity ID in the form context
    setValue('identityId', kyc?.identity?.id as never, { shouldDirty: true });
  }

  // Use the effect hook to call onFetchKyc when KYC data changes
  useEffect(onFetchKyc, [kyc, setValue, isKycSuccess]);

  // Render the appropriate form fields based on the user payment type ID
  switch (typeId?.toString()) {
    case UserPaymentDetailsTypeId.Pix?.toString():
      return <UserPaymentDetailsFormContentPix isPending={isPending} />;
    case UserPaymentDetailsTypeId.Imps?.toString():
      return <UserPaymentDetailsFormContentImps isPending={isPending} />;
    case UserPaymentDetailsTypeId.ArgentineBankAccount?.toString():
    case UserPaymentDetailsTypeId.MexicanBankAccount?.toString():
    case UserPaymentDetailsTypeId.NigerianBankAccount?.toString():
    case UserPaymentDetailsTypeId.SingaporeBankAccount?.toString():
    case UserPaymentDetailsTypeId.SouthAfricaBankAccount?.toString():
    case UserPaymentDetailsTypeId.SouthAfricaBankAccount?.toString():
    case UserPaymentDetailsTypeId.Qris?.toString():
      return <UserPaymentDetailsFormContentBankAccount isPending={isPending} />;
  }
}
